import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type CourseMarks as Marks } from "../api.js";

/**
 * A student's own marks across one course.
 *
 * The question this answers -- "how am I doing?" -- had no page before: marks
 * lived one at a time on individual assignment cards, and an exam's mark lived
 * behind a link the student had to know existed.
 *
 * The running total counts only what has been assessed. A percentage that
 * silently included work not yet set would read as a fail all semester, so what
 * is outstanding and what is awaiting a mark are stated separately rather than
 * folded into the number.
 */
export default function CourseMarks() {
  const { courseId } = useParams<{ courseId: string }>();
  const [marks, setMarks] = useState<Marks | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseId) return;
    api
      .get<Marks>(`/api/courses/${courseId}/my-marks`)
      .then(setMarks)
      .catch((e) => setError(e.message));
  }, [courseId]);

  if (error) {
    return (
      <div className="page">
        <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
        <div className="error-banner" style={{ marginTop: 12 }}>{error}</div>
      </div>
    );
  }
  if (!marks) return <div className="page muted">Loading…</div>;

  const { totals } = marks;

  return (
    <div className="page">
      <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
      <h1 style={{ fontSize: 22, margin: "10px 0 6px" }}>
        {marks.course.code} — my marks
        {/* A finished course's marks are final; say so rather than leaving a
            student to wonder whether something is still to come. */}
        {marks.course.archived && (
          <span className="badge draft" style={{ marginLeft: 8, fontSize: 12 }}>finished</span>
        )}
      </h1>
      <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>{marks.course.name}</div>

      <div className="card">
        <div style={{ fontSize: 28, fontWeight: 600 }}>
          {totals.earned}{" "}
          <span className="muted" style={{ fontSize: 18, fontWeight: 400 }}>/ {totals.outOf}</span>
          {totals.percent !== null && (
            <span className="muted" style={{ fontSize: 18, fontWeight: 400 }}>
              {" "}({totals.percent}%)
            </span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          across {totals.assessedCount} assessment{totals.assessedCount === 1 ? "" : "s"} so far
          {totals.pendingCount > 0 &&
            ` · ${totals.pendingCount} waiting on a mark`}
          {totals.outstandingCount > 0 &&
            ` · ${totals.outstandingCount} not yet attempted`}
        </div>
        <p className="muted" style={{ fontSize: 13, margin: "10px 0 0" }}>
          This counts only work that has been marked and released. It is not a final grade, and
          how your college weighs labs against exams is their decision, not this page's.
        </p>
      </div>

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>ASSIGNMENTS</div>
        {marks.assignments.length === 0 ? (
          <div className="muted">No assignments yet.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Assignment</th><th>Due</th><th>Done</th><th>Mark</th></tr>
            </thead>
            <tbody>
              {marks.assignments.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Link to={`/assignments/${a.id}`}>{a.title}</Link>
                    {a.late && <span className="badge warn" style={{ marginLeft: 6 }}>late</span>}
                  </td>
                  <td className="muted">
                    {a.dueAt ? new Date(a.dueAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="muted">
                    {a.questionCount > 1 ? `${a.answered} of ${a.questionCount}` : a.answered ? "yes" : "—"}
                  </td>
                  <td>
                    {a.earned === null ? (
                      <span className="badge draft">not submitted</span>
                    ) : (
                      <>
                        <strong>{a.earned}</strong>
                        <span className="muted"> / {a.outOf}</span>
                        {a.awaitingMark && (
                          <span className="badge warn" style={{ marginLeft: 6 }}>being marked</span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {marks.exams.length > 0 && (
        <div className="card">
          <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>EXAMS</div>
          <table>
            <thead>
              <tr><th>Exam</th><th>Closed</th><th>Status</th><th>Mark</th></tr>
            </thead>
            <tbody>
              {marks.exams.map((e) => (
                <tr key={e.id}>
                  <td>
                    {e.status === null ? e.title : <Link to={`/exams/${e.id}/result`}>{e.title}</Link>}
                  </td>
                  <td className="muted">{new Date(e.closesAt).toLocaleDateString()}</td>
                  <td className="muted">
                    {e.status === null ? "not sat" : e.status.toLowerCase().replace("_", " ")}
                  </td>
                  <td>
                    {/* Why a mark is absent matters more here than anywhere
                        else: "never published" and "not yet" are different
                        answers, and the server decides which applies. */}
                    {e.earned !== null ? (
                      <>
                        <strong>{e.earned}</strong>
                        <span className="muted"> / {e.outOf}</span>
                      </>
                    ) : e.status === null ? (
                      <span className="badge draft">—</span>
                    ) : e.resultState === "NEVER" ? (
                      <span className="muted">not published</span>
                    ) : e.availableAt ? (
                      <span className="muted">after {new Date(e.availableAt).toLocaleString()}</span>
                    ) : (
                      <span className="badge warn">in progress</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
